import type { FileObject } from '@supabase/storage-js';
import { supabase } from '@/lib/supabaseClient';
import { ensureAuthenticatedSession } from '@/utils/authentication';
import { formatBytes, getFileExtension, formatCategoryLabel } from '@/utils/fileUtils';
import { defaultCategories, categoryNames, Document as UIDocument } from '@/components/documents/DocumentData';
import { getErrorStatus } from '@/utils/errors';
import { isRecord } from '@/utils/typeGuards';
import { sanitizeToSafeFileName } from '@/utils/fileValidation';

const BUCKET_NAME = 'barrio-hub-manager';
const EXCLUDED_CATEGORIES = new Set(['private', 'payments']);
const EXCLUDED_CATEGORY_PREFIXES = ['private/', 'payments/'];

const isExcludedCategory = (category: string): boolean => {
  const normalized = category?.toLowerCase() ?? '';
  if (!normalized) return false;
  if (EXCLUDED_CATEGORIES.has(normalized)) return true;
  return EXCLUDED_CATEGORY_PREFIXES.some(prefix => normalized.startsWith(prefix));
};

export interface CommunityDocument extends UIDocument {
  mimeType: string | null;
  sizeInBytes: number;
}

export interface DocumentMetadataInput {
  communityId: string;
  path: string;
  documentDate?: string | null;
  dueDate?: string | null;
  noticeDate?: string | null;
  uploadedBy?: string | null;
}

interface DocumentMetadataRecord {
  path: string;
  document_date: string | null;
  due_date: string | null;
  notice_date: string | null;
  uploaded_by: string | null;
}

const ensureCategoryLabel = (category: string): string => {
  if (!category) return 'Sin categoría';
  if (categoryNames[category]) return categoryNames[category];
  if (/^\d+$/.test(category)) return 'Otros';
  if (category.includes('-') || category.includes('_')) {
    return formatCategoryLabel(category);
  }
  return category;
};

const normalizeStorageKey = (value: string) => {
  if (!value) return '';

  let normalized = `${value}`
    .trim()
    .replace(/[\\]+/g, '/')
    .replace(/^\/+/g, '')
    .replace(/\/+/g, '/')
    .replace(/\/+$/g, '');

  const bucketPrefix = `${BUCKET_NAME}/`;
  if (normalized === BUCKET_NAME) {
    normalized = '';
  } else if (normalized.startsWith(bucketPrefix)) {
    normalized = normalized.slice(bucketPrefix.length);
  }

  return normalized;
};

const resolveCommunityIdFromPath = (path: string) => {
  const normalized = normalizeStorageKey(path);
  if (!normalized) return '';
  const [communityId] = normalized.split('/').filter(Boolean);
  return communityId ?? '';
};

const sanitizePathSegment = (segment: string) => {
  const trimmed = `${segment ?? ''}`
    .trim()
    .replace(/^[/\\.]+/, '')
    .replace(/[/\\]+$/, '');
  const withoutTraversal = trimmed.replace(/\.\.+/g, '-');
  return withoutTraversal;
};

const sanitizeFileName = (fileName: string) => sanitizeToSafeFileName(fileName);

const ensureCommunitySegment = (communityId: string) => {
  const safeCommunityId = sanitizePathSegment(communityId);
  if (!safeCommunityId) {
    throw new Error('Identificador de comunidad inválido para almacenamiento.');
  }
  return safeCommunityId;
};

const buildPath = (communityId: string, category: string, fileName: string) => {
  const segments = [ensureCommunitySegment(communityId)];
  const safeCategory = sanitizePathSegment(category);
  if (safeCategory) {
    segments.push(safeCategory);
  }
  const safeFileName = sanitizeFileName(fileName);
  segments.push(safeFileName);

  const path = segments.join('/');
  if (path.startsWith('/')) {
    throw new Error('La ruta generada para el archivo no puede comenzar con "/".');
  }
  return path;
};

const getFileMetadata = (
  file: FileObject,
): { size: number; mimetype: string | null } => {
  const metadata = isRecord(file.metadata) ? file.metadata : null;
  const size = typeof metadata?.size === 'number' ? metadata.size : 0;
  const mimetype = typeof metadata?.mimetype === 'string' ? metadata.mimetype : null;
  return { size, mimetype };
};

const mapFileToDocument = (
  communityId: string,
  category: string,
  file: FileObject
): CommunityDocument => {
  const fileName: string = file.name;
  const format = getFileExtension(fileName);
  const metadata = getFileMetadata(file);
  const sizeInBytes = metadata.size;
  const date = file.updated_at ?? file.created_at ?? new Date().toISOString();
  const fallbackPath = [communityId, category, fileName]
    .filter(segment => !!segment)
    .join('/');
  const fallbackStoragePath = normalizeStorageKey(fallbackPath);
  const candidatePathFromId = normalizeStorageKey(file.id ?? '');
  const storagePath = candidatePathFromId &&
    (candidatePathFromId === fallbackStoragePath ||
      candidatePathFromId === communityId ||
      candidatePathFromId.startsWith(`${communityId}/`))
    ? candidatePathFromId
    : fallbackStoragePath;
  const resolvedCategory = (category ?? '') || (() => {
    const communityPrefix = `${communityId.replace(/\/+/g, '/')}/`;
    const withoutCommunity = storagePath.startsWith(communityPrefix)
      ? storagePath.slice(communityPrefix.length)
      : storagePath;
    const segments = withoutCommunity.split('/').filter(Boolean);
    return segments.length > 1 ? segments.slice(0, -1).join('/') : '';
  })();

  return {
    id: storagePath,
    name: fileName,
    type: format || 'file',
    date,
    size: formatBytes(sizeInBytes),
    format,
    category: resolvedCategory,
    path: storagePath,
    mimeType: metadata.mimetype,
    sizeInBytes,
  };
};

const getStringOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value : null;

const fetchDocumentMetadataMap = async (communityId: string) => {
  const communitySegment = ensureCommunitySegment(communityId);
  const { data, error } = await supabase
    .from('document_metadata')
    .select('path, document_date, due_date, notice_date, uploaded_by')
    .eq('community_id', communitySegment);

  if (error) {
    throw new Error(error.message);
  }

  const metadataMap = new Map<string, DocumentMetadataRecord>();
  (data ?? []).forEach((row: unknown) => {
    if (!isRecord(row)) return;
    const path = getStringOrNull(row.path);
    if (path) {
      metadataMap.set(normalizeStorageKey(path), {
        path,
        document_date: getStringOrNull(row.document_date),
        due_date: getStringOrNull(row.due_date),
        notice_date: getStringOrNull(row.notice_date),
        uploaded_by: getStringOrNull(row.uploaded_by),
      });
    }
  });

  return metadataMap;
};

const applyMetadataDates = (
  documents: CommunityDocument[],
  metadataMap: Map<string, DocumentMetadataRecord>
) =>
  documents.map(document => {
    const meta = metadataMap.get(normalizeStorageKey(document.path));
    if (!meta) return document;
    return {
      ...document,
      date: meta.document_date || document.date,
      dueDate: meta.due_date,
      noticeDate: meta.notice_date,
      uploadedBy: meta.uploaded_by,
    };
  });

const listFilesInCategory = async (
  communityId: string,
  category: string
): Promise<CommunityDocument[]> => {
  await ensureAuthenticatedSession();
  if (isExcludedCategory(category)) {
    return [];
  }
  const communitySegment = ensureCommunitySegment(communityId);
  const categorySegment = sanitizePathSegment(category);
  const path = categorySegment
    ? `${communitySegment}/${categorySegment}`
    : communitySegment;

  const { data, error } = await supabase.storage.from(BUCKET_NAME).list(path, {
    limit: 1000,
    sortBy: { column: 'name', order: 'asc' },
  });

  if (error) {
    throw new Error(error.message);
  }

  return (
    data ?? []
  )
    .filter(item => item && item.id && item.metadata)
    .map(file => mapFileToDocument(communitySegment, categorySegment, file));
};

export const getCommunityDocuments = async (
  communityId: string,
  options?: {
    categories?: string[];
  }
): Promise<Record<string, CommunityDocument[]>> => {
  await ensureAuthenticatedSession();
  const communitySegment = ensureCommunitySegment(communityId);
  const metadataMap = await fetchDocumentMetadataMap(communitySegment);
  const baseCategories = options?.categories ?? defaultCategories;
  const categoriesSet = new Set(
    baseCategories
      .map(category => `${category ?? ''}`.trim())
      .filter(category => category.length > 0)
  );

  const { data, error } = await supabase.storage.from(BUCKET_NAME).list(communitySegment, {
    limit: 1000,
    sortBy: { column: 'name', order: 'asc' },
  });

  if (error) {
    const status = getErrorStatus(error);
    if (status === 404) {
      const empty: Record<string, CommunityDocument[]> = {};
      categoriesSet.forEach(category => {
        empty[category] = [];
      });
      return empty;
    }
    throw new Error(error.message);
  }

  const result: Record<string, CommunityDocument[]> = {};

  const directories = (data ?? [])
    .filter(item => !item.metadata)
    .filter(dir => !isExcludedCategory(dir?.name ?? ''));
  const filesInRoot = (data ?? []).filter(item => item.metadata);

  if (filesInRoot.length > 0) {
    const uncategorized = '';
    categoriesSet.add(uncategorized);
    result[uncategorized] = applyMetadataDates(
      filesInRoot.map(file =>
        mapFileToDocument(communitySegment, uncategorized, file)
      ),
      metadataMap
    );
  }

  for (const dir of directories) {
    const category = dir.name;
    if (isExcludedCategory(category)) {
      continue;
    }
    categoriesSet.add(category);
    try {
      const documents = await listFilesInCategory(communitySegment, category);
      result[category] = applyMetadataDates(documents, metadataMap);
    } catch (catError) {
      console.error(catError);
      result[category] = [];
    }
  }

  for (const category of categoriesSet) {
    if (!(category in result)) {
      result[category] = [];
    }
  }

  return result;
};

export const uploadCommunityDocument = async (
  communityId: string,
  category: string,
  file: File,
  options?: {
    allowPrivate?: boolean;
    dueDate?: string | null;
    noticeDate?: string | null;
    documentDate?: string | null;
  }
) => {
  const session = await ensureAuthenticatedSession();
  const currentUserId = session?.user?.id ?? null;

  if (!options?.allowPrivate && isExcludedCategory(category)) {
    throw new Error('No está permitido subir archivos en esta ruta.');
  }
  if (!(file instanceof Blob)) {
    throw new Error('El archivo seleccionado no es válido.');
  }

  const safeFileName = sanitizeFileName(file.name);
  const objectPath = normalizeStorageKey(
    buildPath(communityId, category, safeFileName)
  );
  const normalizedFile =
    file instanceof File && file.name === safeFileName
      ? file
      : new File([file], safeFileName, {
          type: file.type || 'application/octet-stream',
        });

  const { error } = await supabase.storage
    .from(BUCKET_NAME)
    .upload(objectPath, normalizedFile, {
      upsert: true,
      cacheControl: '3600',
      contentType: normalizedFile.type || 'application/octet-stream',
    });

  if (error) {
    const status = getErrorStatus(error);
    const reason = error.message ?? 'No se pudo subir el archivo.';
    throw new Error(
      status ? `(${status}) ${reason}` : reason
    );
  }

  // Save metadata if any dates or uploader are provided
  await setDocumentMetadataDate({
    communityId,
    path: objectPath,
    documentDate: options?.documentDate,
    dueDate: options?.dueDate,
    noticeDate: options?.noticeDate,
    uploadedBy: currentUserId,
  });
};

export const deleteCommunityDocument = async (path: string) => {
  await ensureAuthenticatedSession();
  const normalizedPath = normalizeStorageKey(path);
  const communityId = resolveCommunityIdFromPath(normalizedPath);
  const { error } = await supabase.storage
    .from(BUCKET_NAME)
    .remove([normalizedPath]);
  if (error) {
    throw new Error(error.message);
  }

  if (communityId) {
    const { error: metadataError } = await supabase
      .from('document_metadata')
      .delete()
      .eq('community_id', communityId)
      .eq('path', normalizedPath);
    if (metadataError) {
      throw new Error(metadataError.message);
    }
  }
};

export const moveCommunityDocument = async (
  communityId: string,
  document: CommunityDocument,
  newCategory: string
) => {
  await ensureAuthenticatedSession();
  if (isExcludedCategory(newCategory)) {
    throw new Error('No está permitido mover archivos a esta ruta.');
  }
  const destinationPath = buildPath(communityId, newCategory, document.name);
  const normalizedDestination = normalizeStorageKey(destinationPath);
  const sourcePath = normalizeStorageKey(document.path);

  if (normalizedDestination === sourcePath) {
    return;
  }

  const { error } = await supabase.storage
    .from(BUCKET_NAME)
    .move(sourcePath, normalizedDestination);

  if (error) {
    throw new Error(error.message);
  }

  await updateDocumentMetadataPath({
    communityId,
    previousPath: sourcePath,
    nextPath: normalizedDestination,
  });

  return normalizedDestination;
};

export const renameCommunityDocument = async (
  communityId: string,
  document: CommunityDocument,
  newName: string
) => {
  await ensureAuthenticatedSession();
  const sanitizedName = sanitizeFileName(newName);
  const destinationPath = buildPath(communityId, document.category, sanitizedName);
  const normalizedDestination = normalizeStorageKey(destinationPath);
  const normalizedSource = normalizeStorageKey(document.path);

  if (normalizedDestination === normalizedSource) {
    return;
  }

  const { error } = await supabase.storage
    .from(BUCKET_NAME)
    .move(normalizedSource, normalizedDestination);

  if (error) {
    throw new Error(error.message);
  }

  await updateDocumentMetadataPath({
    communityId,
    previousPath: normalizedSource,
    nextPath: normalizedDestination,
  });

  return normalizedDestination;
};

export interface AdminDocumentUpdateInput {
  path: string;
  name: string;
  category: string;
  communityId: string;
}

export interface AdminDocumentUpdatePayload {
  name: string;
  category: string;
  communityId: string;
}

export const updateAdminDocumentDetails = async (
  original: AdminDocumentUpdateInput,
  updates: AdminDocumentUpdatePayload
) => {
  await ensureAuthenticatedSession();
  const targetCommunity = ensureCommunitySegment(updates.communityId);
  const targetCategory = sanitizePathSegment(updates.category);
  const sanitizedName = sanitizeFileName(updates.name);
  const destinationPath = buildPath(targetCommunity, targetCategory, sanitizedName);
  const normalizedDestination = normalizeStorageKey(destinationPath);
  const normalizedSource = normalizeStorageKey(original.path);

  let finalPath = normalizedSource;

  if (normalizedDestination !== normalizedSource) {
    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .move(normalizedSource, normalizedDestination);
    if (error) {
      throw new Error(error.message);
    }
    finalPath = normalizedDestination;
  }

  await updateDocumentMetadataRecord({
    previousCommunityId: original.communityId,
    nextCommunityId: updates.communityId,
    previousPath: normalizedSource,
    nextPath: finalPath,
  });

  return {
    path: finalPath,
    name: sanitizedName,
    category: targetCategory,
    communityId: updates.communityId,
  };
};

export const setDocumentMetadataDate = async ({
  communityId,
  path,
  documentDate,
  dueDate,
  noticeDate,
  uploadedBy,
}: DocumentMetadataInput) => {
  const session = await ensureAuthenticatedSession();
  const communitySegment = ensureCommunitySegment(communityId);
  const normalizedPath = normalizeStorageKey(path);
  const normalizedDate = documentDate?.trim() ? documentDate.trim() : null;
  const normalizedDueDate = dueDate?.trim() ? dueDate.trim() : null;
  const normalizedNoticeDate = noticeDate?.trim() ? noticeDate.trim() : null;
  const targetUserId = uploadedBy || session?.user?.id || null;

  const payload: Record<string, unknown> = {
    community_id: communitySegment,
    path: normalizedPath,
    document_date: normalizedDate,
    due_date: normalizedDueDate,
    notice_date: normalizedNoticeDate,
  };

  if (targetUserId) {
    payload.uploaded_by = targetUserId;
  }

  const { error } = await supabase.from('document_metadata').upsert(
    payload,
    { onConflict: 'community_id,path' }
  );

  if (error) {
    throw new Error(error.message);
  }
};

const updateDocumentMetadataPath = async ({
  communityId,
  previousPath,
  nextPath,
}: {
  communityId: string;
  previousPath: string;
  nextPath: string;
}) => {
  const normalizedPrevious = normalizeStorageKey(previousPath);
  const normalizedNext = normalizeStorageKey(nextPath);
  if (!normalizedPrevious || normalizedPrevious === normalizedNext) return;
  const communitySegment = ensureCommunitySegment(communityId);
  const { error } = await supabase
    .from('document_metadata')
    .update({ path: normalizedNext })
    .eq('community_id', communitySegment)
    .eq('path', normalizedPrevious);
  if (error) {
    throw new Error(error.message);
  }
};

const updateDocumentMetadataRecord = async ({
  previousCommunityId,
  nextCommunityId,
  previousPath,
  nextPath,
}: {
  previousCommunityId: string;
  nextCommunityId: string;
  previousPath: string;
  nextPath: string;
}) => {
  const normalizedPrevious = normalizeStorageKey(previousPath);
  const normalizedNext = normalizeStorageKey(nextPath);
  const previousCommunity = ensureCommunitySegment(previousCommunityId);
  const nextCommunity = ensureCommunitySegment(nextCommunityId);

  if (
    normalizedPrevious === normalizedNext &&
    previousCommunity === nextCommunity
  ) {
    return;
  }

  const { error } = await supabase
    .from('document_metadata')
    .update({
      community_id: nextCommunity,
      path: normalizedNext,
    })
    .eq('community_id', previousCommunity)
    .eq('path', normalizedPrevious);

  if (error) {
    throw new Error(error.message);
  }
};

export const getSignedDocumentUrl = async (path: string, expiresInSeconds = 3600) => {
  await ensureAuthenticatedSession();
  const normalizedPath = normalizeStorageKey(path);
  const { data, error } = await supabase.storage
    .from(BUCKET_NAME)
    .createSignedUrl(normalizedPath, expiresInSeconds);

  if (error) {
    throw new Error(error.message);
  }

  return data.signedUrl;
};

export const getCategoryDisplayName = (category: string) => ensureCategoryLabel(category);
